import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { SelectProps } from '../../models';
import styles from './select.module.scss';
import IconsColor from '../../util/iconsColor';

/** Sentinel prefix used by callers (e.g. JourneyDetails trip picker) to mark non-selectable
 *  group-section headers inside the options list. */
const GROUP_HEADER_VALUE_PREFIX = '__header__';
const isGroupHeaderOption = (opt: { value: string | number | undefined | null }): boolean =>
  String(opt?.value || '').startsWith(GROUP_HEADER_VALUE_PREFIX);

const Select: React.FC<SelectProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  disabled = false,
  required = false,
  error,
  warning,
  width = '100%',
  height = '44px',
  name,
  id,
  onOpen,
  isOtherOption,
  otherValue = '',
  onOtherChange,
  otherPlaceholder = 'Other',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownDirection, setDropdownDirection] = useState<'down' | 'up'>('down');
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(240);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const optionsListRef = useRef<HTMLUListElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectId = id || `select-${label.toLowerCase().replace(/\s+/g, '-')}`;

  const otherOptions = useMemo(
    () => (isOtherOption ? options.filter((opt) => isOtherOption(opt)) : []),
    [options, isOtherOption]
  );
  const selectableOptions = useMemo(
    () => (isOtherOption ? options.filter((opt) => !isOtherOption(opt)) : options),
    [options, isOtherOption]
  );

  // Get selected option label. Always derived from the FULL options list (not the filtered
  // one) so the trigger keeps showing the selected label after the user clears the search.
  const selectedOption = options.find((opt) => String(opt.value) === String(value));
  const selectedLabel = (otherValue || '').trim() || (selectedOption ? selectedOption.label : '');

  // Typeahead filter. Empty search -> all options as-is (preserves existing behavior).
  // Otherwise: case-insensitive substring match on label. Group-header rows survive only if
  // at least one non-header option that follows them (before the next header) matches —
  // keeps grouped lists readable while filtering. "Other" text-field rows are excluded here
  // and rendered as a pinned footer instead.
  const filteredOptions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return selectableOptions;
    const out: typeof selectableOptions = [];
    let pendingHeader: (typeof selectableOptions)[number] | null = null;
    for (const opt of selectableOptions) {
      if (isGroupHeaderOption(opt)) {
        // Defer adding the header until we know one of its children matches.
        pendingHeader = opt;
        continue;
      }
      const labelMatch = String(opt.label ?? '').toLowerCase().includes(q);
      if (!labelMatch) continue;
      if (pendingHeader) {
        out.push(pendingHeader);
        pendingHeader = null;
      }
      out.push(opt);
    }
    return out;
  }, [selectableOptions, searchTerm]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inWrapper = Boolean(wrapperRef.current && wrapperRef.current.contains(target));
      const inDropdown = Boolean(dropdownRef.current && dropdownRef.current.contains(target));
      if (!inWrapper && !inDropdown) {
        setIsOpen(false);
        setHighlightedIndex(-1);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Auto-focus the search input when the dropdown opens, so the user can start typing
  // immediately. Mirrors Select2 behavior. Runs after the dropdown is mounted in the portal.
  useEffect(() => {
    if (!isOpen) return;
    // setTimeout to defer past the portal mount on the same tick.
    const timer = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [isOpen]);

  // Filtered list shrinks/grows on each keystroke — any previously held highlight index could
  // point past the new list, so reset it. The user can re-arrow into the smaller list.
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  const updateDropdownPlacement = useCallback(() => {
    if (!wrapperRef.current) return;
    const selectRect = wrapperRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Spacing constants: GAP = gap between trigger and popup, MARGIN = min distance
    // we keep from the viewport edge, PREFERRED_MAX = comfortable popup height so it
    // doesn't grow into a full-screen list when there's lots of room.
    const GAP = 4;
    const MARGIN = 8;
    const MIN_HEIGHT = 180;
    const PREFERRED_MAX = 320;

    // Usable vertical room on each side, already accounting for the gap + edge margin.
    const spaceBelow = viewportHeight - selectRect.bottom - GAP - MARGIN;
    const spaceAbove = selectRect.top - GAP - MARGIN;

    // Open upward only when below can't comfortably fit and above genuinely has more room.
    const openUp = spaceBelow < MIN_HEIGHT && spaceAbove > spaceBelow;
    const available = Math.max(0, openUp ? spaceAbove : spaceBelow);

    // This is the max-height of the ENTIRE popup (search row + options list together).
    // Capped to `available` so it can never overflow the viewport, and to PREFERRED_MAX
    // so it stays compact. The options list scrolls internally via flexbox (see SCSS).
    const maxHeight = Math.floor(Math.min(available, PREFERRED_MAX));

    const width = Math.max(160, Math.floor(selectRect.width));
    const left = Math.max(MARGIN, Math.min(selectRect.left, viewportWidth - width - MARGIN));
    const top = openUp ? selectRect.top - GAP : selectRect.bottom + GAP;

    setDropdownDirection(openUp ? 'up' : 'down');
    setDropdownMaxHeight(maxHeight);
    setDropdownPosition({ top, left, width });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updateDropdownPlacement();
    const handleReposition = () => updateDropdownPlacement();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen, updateDropdownPlacement]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionsListRef.current) {
      const allOptions = Array.from(optionsListRef.current.children);
      const highlightedElement = allOptions[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    }
  }, [highlightedIndex]);

  const handleToggleDropdown = () => {
    if (!disabled) {
      setIsOpen((prev) => {
        const next = !prev;
        if (next) onOpen?.();
        return next;
      });
      setHighlightedIndex(-1);
      setSearchTerm('');
    }
  };

  const fireChange = (optionValue: string) => {
    const syntheticEvent = {
      target: { value: optionValue, name: name || selectId },
      currentTarget: { value: optionValue, name: name || selectId },
    } as React.ChangeEvent<HTMLSelectElement>;
    onChange(syntheticEvent);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setHighlightedIndex(-1);
    setSearchTerm('');
  };

  const handleSelectOption = (optionValue: string, optionDisabled?: boolean) => {
    if (optionDisabled) return;
    const pickedOther = otherOptions.some((opt) => String(opt.value) === String(optionValue));
    if (!pickedOther) onOtherChange?.('');
    fireChange(optionValue);
    closeDropdown();
  };

  const handleOtherInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value;
    onOtherChange?.(text);
    const otherId = otherOptions[0] != null ? String(otherOptions[0].value) : '';
    if (!otherId) return;
    if (text.trim()) {
      if (String(value) !== otherId) fireChange(otherId);
    } else if (String(value) === otherId) {
      fireChange('');
    }
  };

  const handleOtherKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.key === 'Enter' || event.key === 'Escape') {
      event.preventDefault();
      closeDropdown();
    }
    if (event.key === 'Tab') {
      closeDropdown();
    }
  };

  // Keyboard nav now operates on the FILTERED list (after typeahead). When no filter is
  // active, filteredOptions === options, so behavior matches the pre-typeahead version.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0) {
          const option = filteredOptions[highlightedIndex];
          if (option && !option.disabled) {
            handleSelectOption(option.value as string);
          }
        } else if (!isOpen) {
          setIsOpen(true);
          onOpen?.();
        }
        break;
      // NOTE: Space is intentionally NOT a select/open shortcut anymore — it must reach the
      // search input so the user can type spaces in their query.
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          onOpen?.();
        } else {
          setHighlightedIndex((prev) => {
            let next = prev + 1;
            while (next < filteredOptions.length && filteredOptions[next].disabled) {
              next++;
            }
            return next < filteredOptions.length ? next : prev;
          });
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex((prev) => {
            let next = prev - 1;
            while (next >= 0 && filteredOptions[next].disabled) {
              next--;
            }
            return next >= 0 ? next : 0;
          });
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        setSearchTerm('');
        break;
      case 'Tab':
        if (isOpen) {
          setIsOpen(false);
          setHighlightedIndex(-1);
          setSearchTerm('');
        }
        break;
      default:
        break;
    }
  };

  return (
    <div
      className={label?.length > 0 ? styles.selectLabalWrapper : styles.selectWrapper}
      style={{ width }}
      ref={wrapperRef}
      onKeyDown={handleKeyDown}
    >
      <label htmlFor={selectId} className={styles.selectLabel}>
        {label}
        {required && <span className={styles.requiredIndicator}>*</span>}
      </label>

      <div className={styles.selectContainer}>
        <div
          className={styles.selectControl + ' ' + (isOpen ? styles.open : '') + ' ' + (error ? styles.error : '') + ' ' + (warning ? styles.warning : '') + ' ' + (disabled ? styles.disabled : '')}
          onClick={handleToggleDropdown}
          tabIndex={disabled ? -1 : 0}
          style={{ minHeight: height }}
          role="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className={styles.selectValue + ' ' + (!selectedLabel ? styles.placeholder : '')}>
            {selectedLabel || placeholder}
          </span>
          <div className={styles.selectIcon + ' ' + (isOpen ? styles.open : '')}>
            <IconsColor iconName="chevronDown" width="20px" height="20px" fillColor="none" strokeColor="currentColor" />
          </div>
        </div>

        {isOpen && createPortal(
          <div
            className={styles.selectDropdown + ' ' + (dropdownDirection === 'up' ? styles.dropdownUp : styles.dropdownDown)}
            ref={dropdownRef}
            style={{
              position: 'fixed',
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              top: dropdownDirection === 'up' ? 'auto' : `${dropdownPosition.top}px`,
              bottom: dropdownDirection === 'up' ? `${Math.max(8, window.innerHeight - dropdownPosition.top)}px` : 'auto',
              // Cap the WHOLE popup (search row + list). Flexbox lets only the list scroll.
              maxHeight: `${dropdownMaxHeight}px`,
            }}
          >
            {/* Search input — always rendered when dropdown is open. Typing filters the list
                below (see filteredOptions). onClick stopPropagation so clicking inside the
                input doesn't bubble up to a parent that might toggle the dropdown closed. */}
            <div className={styles.selectSearch}>
              <input
                ref={searchInputRef}
                type="text"
                className={styles.selectSearchInput}
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
              <div className={styles.selectSearchIcon}>
                <IconsColor iconName="searchIcon" width="16px" height="16px" fillColor="none" strokeColor="currentColor" />
              </div>
            </div>

            <ul className={styles.selectOptions} ref={optionsListRef} role="listbox">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => (
                  (() => {
                    const isGroupHeader = isGroupHeaderOption(option)
                    const isSelected = String(option.value) === String(value)
                    return (
                      <li
                        key={option.opction_id || option.value}
                        className={
                          styles.selectOption + ' ' +
                          (isSelected ? styles.selected : '') + ' ' +
                          (index === highlightedIndex ? styles.highlighted : '') + ' ' +
                          (option.disabled ? styles.disabled : '') + ' ' +
                          (isGroupHeader ? styles.optionGroupHeader : '')
                        }
                        onClick={() => handleSelectOption(option.value as string, option.disabled)}
                        onMouseEnter={() => !option.disabled && setHighlightedIndex(index)}
                        role="option"
                        aria-selected={isSelected}
                        aria-disabled={option.disabled}
                      >
                        {option.label}
                        {isSelected && (
                          <div className={styles.selectCheckIcon}>
                            <IconsColor iconName="checkIcon" width="16px" height="16px" fillColor="none" strokeColor="currentColor" />
                          </div>
                        )}
                      </li>
                    )
                  })()
                ))
              ) : otherOptions.length === 0 ? (
                <li className={styles.selectNoOptions}>
                  {options.length === 0 ? 'No options available' : 'No matches found'}
                </li>
              ) : null}
            </ul>
            {otherOptions.length > 0 && (
              <div className={styles.selectOtherRow} onClick={(e) => e.stopPropagation()}>
                <input
                  type="text"
                  className={styles.selectOtherInput}
                  placeholder={otherPlaceholder}
                  value={otherValue}
                  onChange={handleOtherInputChange}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={handleOtherKeyDown}
                />
              </div>
            )}
          </div>,
          document.body
        )}
      </div>

      {/* Hidden input for form submission */}
      <input type="hidden" name={name || selectId} value={value} />

      {error && (
        <span className={styles.message + ' ' + styles.errorMessage}>
          <IconsColor iconName="errorIcon" width="16px" height="16px" fillColor="none" strokeColor="currentColor" />
          {error}
        </span>
      )}

      {warning && !error && (
        <span className={styles.message + ' ' + styles.warningMessage}>
          <IconsColor iconName="warningIcon" width="16px" height="16px" fillColor="none" strokeColor="currentColor" />
          {warning}
        </span>
      )}
    </div>
  );
};

export default Select;
